import {useMutation} from '@tanstack/react-query';
import {UseFormReturn} from 'react-hook-form';
import {BackendResponse} from '../../http/backend-response/backend-response';
import {onFormQueryError} from '../../errors/on-form-query-error';
import {apiClient} from '../../http/query-client';
import {useAuth} from '../use-auth';
import {useCallback} from 'react';

interface LoginResponse extends BackendResponse {
  bootstrapData: string;
  two_factor: false;
  redirectUrl?: string;
}
interface TwoFactorResponse {
  two_factor: true;
}

type Response = LoginResponse | TwoFactorResponse;

export interface LoginPayload {
  email: string;
  password: string;
  remember: boolean;
  token_name: string;
}

export function useLogin(form: UseFormReturn<LoginPayload>) {
  const handleSuccess = useHandleLoginSuccess();
  return useMutation({
    mutationFn: login,
    onSuccess: response => {
      if (!response.two_factor) {
        handleSuccess(response);
      }
    },
    onError: r => onFormQueryError(r, form),
  });
}

export function useHandleLoginSuccess() {
  const {getRedirectUri} = useAuth();

  return useCallback(
    (response: LoginResponse) => {
      // Parse bootstrapData to check user subscription status
      let user;
      try {
        const parsed = JSON.parse(response.bootstrapData);
        user = parsed.user;
      } catch {
        user = undefined;
      }

      // Check if user is an admin (has admin permission)
      const isAdmin = user?.permissions?.some(
        (p: {name: string}) => p.name === 'admin',
      );

      // If user arrived via OAuth (e.g. MCP connector), redirect back to authorize
      if (response.redirectUrl) {
        window.location.href = response.redirectUrl;
        return;
      }

      // Full page reload to ensure fresh bootstrap data and clear all cached state
      // Admins and subscribed users go to dashboard; others go to pricing
      if (isAdmin || user?.autumn_subscription_active) {
        window.location.href = getRedirectUri();
      } else {
        window.location.href = '/pricing';
      }
    },
    [getRedirectUri],
  );
}

function login(payload: LoginPayload): Promise<Response> {
  return apiClient.post('auth/login', payload).then(response => response.data);
}
